import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardService } from '../services/dashboard';
import { ScopedEquipmentList } from '../models/DashboardDetails';

type Props = {
  scopeType: 'group' | 'location';
  scopeId?: number;
  currentUser?: any;
};

const LIMIT = 10;

export const useScopedDashboard = ({ scopeType, scopeId, currentUser }: Props) => {
  const [equipmentList, setEquipmentList] = useState<ScopedEquipmentList.Item[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const loadingRef = useRef(false);

  useEffect(() => {
    setEquipmentList([]);
    setPage(1);
    setHasMore(true);
    setTotal(0);
    setError(null);
  }, [scopeType, scopeId]);

  useEffect(() => {
    if (!scopeId || !currentUser) return;

    if (!hasMore && page > 1) return;

    setLoading(true);
    loadingRef.current = true;
    
    const controller = new AbortController();

    const fetchEquipments = async () => {
      try {
        const response = await DashboardService.getScopedEquipmentList({
          scopeType: scopeType === 'group' ? 'STORE' : 'LOCATION',
          scopeId: scopeId,
          page,
          limit: LIMIT,
        });

        if (controller.signal.aborted) return;

        const newItems = response.equipmentList || [];
        const totalItems = response.total || 0;

        setTotal(totalItems);

        setEquipmentList(prev => {
          if (page === 1) return newItems;
          
          const newUniqueItems = newItems.filter(
            newItem => !prev.some(existing => existing.id === newItem.id)
          );
          return [...prev, ...newUniqueItems];
        });

        const currentCount = (page - 1) * LIMIT + newItems.length;
        setHasMore(currentCount < totalItems && newItems.length > 0);

      } catch (err: any) {
        if (!controller.signal.aborted) {
          console.error(err);
          setError('Failed to load equipments');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          loadingRef.current = false;
        }
      }
    };

    fetchEquipments();

    return () => {
      controller.abort();
    };
  }, [scopeId, scopeType, currentUser, page, refreshIndex]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) {
      setPage(prev => prev + 1);
    }
  }, [hasMore]);

  return {
    equipmentList,
    total,
    loading,
    error,
    hasMore,
    loadMore,
    refresh: useCallback(() => {
      setEquipmentList([]);
      setTotal(0);
      setError(null);
      setPage(1);
      setHasMore(true);
      setRefreshIndex(prev => prev + 1);
    }, [])
  };
};
